'use client'

import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { Root, Slottable } from '@radix-ui/react-slot'
import { cn } from '@repo/ui/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'
import { MoveDownIcon, PlusIcon } from 'lucide-react'
import { ComponentPropsWithoutRef, createContext, useContext } from 'react'

type ArrowType = 'arrow' | 'plus-min'

const AccordionContext = createContext<{ arrowType: ArrowType }>({ arrowType: 'arrow' })

const accordionItemVariants = cva('', {
  variants: {
    // The auto variants adapt to the current theme (light or dark) whilst
    // static variants don't (e.g. for usage in sheets and dialogs).
    variant: {
      defaultStatic: 'bg-card border-t border-white',
      defaultAuto: 'bg-secondary border-secondary border-t',
      lightStatic: 'border-border border-t bg-white text-black',
      minimal: '',
    },
    rounded: {
      true: 'first-of-type:rounded-t-lg first-of-type:border-t-0 last-of-type:rounded-b-lg',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'defaultStatic',
    rounded: true,
  },
})

function Accordion({ arrowType = 'arrow', ...props }: { arrowType?: ArrowType } & ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>) {
  return (
    <AccordionContext.Provider value={{ arrowType }}>
      <AccordionPrimitive.Root data-slot='accordion' {...props} />
    </AccordionContext.Provider>
  )
}

function AccordionItem({ className, variant, rounded, ...props }: ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & VariantProps<typeof accordionItemVariants>) {
  return <AccordionPrimitive.Item data-slot='accordion-item' className={cn(accordionItemVariants({ variant, rounded }), className)} {...props} />
}

const accordionTriggerVariants = cva(
  'focus-visible:miista-focus flex flex-1 select-none items-start justify-between uppercase transition-all ease-out hover:underline disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      size: {
        small: 'pb-2 pt-1',
        default: 'px-3 pb-6 pt-2',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

function AccordionTrigger({ className, size, children, ...props }: ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & VariantProps<typeof accordionTriggerVariants>) {
  const { arrowType } = useContext(AccordionContext)

  return (
    <AccordionPrimitive.Header className='flex'>
      <AccordionPrimitive.Trigger
        data-slot='accordion-trigger'
        className={cn(
          accordionTriggerVariants({ size }),
          arrowType === 'arrow' ? '[&[data-state=open]>svg]:rotate-180' : '[&[data-state=open]>svg>path:last-child]:hidden',
          className,
        )}
        {...props}
      >
        {children}
        {arrowType === 'arrow' ? (
          <MoveDownIcon className='relative top-px size-2.5 shrink-0 transition-transform duration-200 ease-out' />
        ) : (
          <PlusIcon className='relative top-px size-2.5 shrink-0' />
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionLinkTrigger({
  className,
  size,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & VariantProps<typeof accordionTriggerVariants>) {
  return (
    <AccordionPrimitive.Header className='flex'>
      <AccordionPrimitive.Trigger data-slot='accordion-trigger' className={cn(accordionTriggerVariants({ size }), 'no-underline', className)} asChild={true} {...props}>
        <Root>
          <Slottable>{children}</Slottable>
          <MoveDownIcon className='relative top-px size-2.5 shrink-0 -rotate-90' />
        </Root>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({ className, size = 'default', children, ...props }: ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & { size?: 'small' | 'default' }) {
  return (
    <AccordionPrimitive.Content
      data-slot='accordion-content'
      className='data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden'
      {...props}
    >
      <div className={cn(size === 'small' ? 'pb-4 pt-0' : 'px-3 pb-4 pt-0', className)}>{children}</div>
    </AccordionPrimitive.Content>
  )
}

export { Accordion, AccordionContent, AccordionItem, AccordionLinkTrigger, AccordionTrigger }
